import React, { Dispatch, SetStateAction, useContext } from 'react';
import { DeleteModalView, DeleteModalViewProps } from './DeleteModal.view';
import { PlanUsageType, RemoveSocialProfileMutationFn, UsageFieldsFragmentDoc } from '../../../../models/graphql';
import { showToast } from '../../../../helpers/showToast';
import { ApolloError, useApolloClient } from '@apollo/client';
import { RemoveProfileConfirmationType } from '../Profiles.controller';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { GetSocialProfiles_workspace_socialProfiles } from '../../../../models/legacy-graphql';
import {
  PostingPlanFiltersEnum,
  PostLibraryFiltersEnum,
  PostScheduleFiltersEnum,
  removeRefFilterValueFromFiltersLocalStorage
} from '../../../../helpers/filterStorageHelpers';
import { AppContext } from '../../../../providers/AppProvider';

export type DeleteModalControllerProps = Omit<DeleteModalViewProps, 'closeDeleteProfileModal' | 'deleteProfile'> & {
  // extend your DeleteModalViewProps
  removeSocialProfileMutation: RemoveSocialProfileMutationFn;
  setRemoveProfileModal: Dispatch<SetStateAction<RemoveProfileConfirmationType>>;
};

export const DeleteModalController = (props: DeleteModalControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { cache: apolloCache } = useApolloClient();
  const { selectedWorkspaceId } = useContext(AppContext);
  const deleteProfile = (
    profileId: string,
    typename: GetSocialProfiles_workspace_socialProfiles['__typename']
  ): void => {
    profileId &&
      typename &&
      props
        .removeSocialProfileMutation({
          variables: { socialProfileId: profileId },
          update: () => {
            removeRefFilterValueFromFiltersLocalStorage(
              [
                { group: 'PostLibraryFilters', key: PostLibraryFiltersEnum.postScheduleProfileFilter },
                { group: 'PostScheduleFilters', key: PostScheduleFiltersEnum.postScheduleProfileFilter },
                { group: 'PostingPlanFilters', key: PostingPlanFiltersEnum.profileFilter }
              ],
              profileId,
              selectedWorkspaceId
            );
          }
        })
        .then(() => {
          closeDeleteProfileModal();
          showToast({
            severity: 'success',
            summary: 'Profile Removed',
            detail: `Social Profile '${props.removeProfileModal.profileName}' has been removed successfully.`
          });
          const cacheId = apolloCache.identify({ id: profileId, __typename: typename });
          if (cacheId) {
            apolloCache.evict({ id: cacheId });
            apolloCache.gc();
          }
          const oldUsage: PlanUsageType | null = apolloCache.readFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}'
          });
          const newUsage = { ...oldUsage, socialProfiles: (oldUsage?.socialProfiles ?? 0) - 1 };
          apolloCache.writeFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}',
            data: newUsage
          });
        })
        .catch(e => {
          const err = e as ApolloError;
          const message = getApolloErrorMessage(err);
          if (message === 'NOT_FOUND') {
            // make sure we clean from cache profile that doesn't exists
            const cacheId = apolloCache.identify({ id: profileId, __typename: typename });
            if (cacheId) {
              apolloCache.evict({ id: cacheId });
              apolloCache.gc();
            }
            // close modal since that profile does not exist and we showed a toast message
            closeDeleteProfileModal();
          }
          showToast({
            severity: 'error',
            summary: 'Remove Profile',
            detail: remapApolloErrors(err)
          });
        });
  };

  const closeDeleteProfileModal = (): void => {
    props.setRemoveProfileModal({
      show: false,
      profileId: '',
      profileName: '',
      typename: undefined
    });
  };

  const viewProps: DeleteModalViewProps = { ...props, deleteProfile, closeDeleteProfileModal };
  return <DeleteModalView {...viewProps} />;
};
